
import { useState, useRef } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface VideoTestimonial {
  id: number;
  name: string;
  role: string;
  location: string;
  thumbnail: string;
  videoUrl: string;
  quote: string;
  classAttended: string;
  duration: string;
}

const videoTestimonials: VideoTestimonial[] = [
  {
    id: 1,
    name: 'Michelle Chen',
    role: 'Marketing Director',
    location: 'Sydney',
    thumbnail: 'https://public.readdy.ai/ai/img_res/fe1a9cbedff82e9680f09ae886c5a9ab.jpg',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    quote: 'The advanced mixology class completely transformed how I entertain at home. My cocktail parties are now legendary!',
    classAttended: 'Advanced Mixology Masterclass',
    duration: '2:34',
  },
  {
    id: 2,
    name: 'David & Lisa Thompson',
    role: 'Couple',
    location: 'Melbourne',
    thumbnail: 'https://readdy.ai/api/search-image?query=happy%20couple%20in%20their%2040s%20making%20cocktails%20together%20at%20cocktail%20class%2C%20laughing%20and%20having%20fun%2C%20romantic%20atmosphere%2C%20warm%20lighting%2C%20lifestyle%20photography%2C%20genuine%20joy&width=600&height=400&seq=video-test-002&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    quote: 'We did the couples cocktail experience for our anniversary. It was the most fun date night we have ever had!',
    classAttended: 'Couples Cocktail Experience',
    duration: '1:58',
  },
  {
    id: 3,
    name: 'Rebecca Martinez',
    role: 'Bride-to-be',
    location: 'Brisbane',
    thumbnail: 'https://readdy.ai/api/search-image?query=group%20of%20happy%20women%20at%20hens%20party%20cocktail%20class%2C%20bride%20wearing%20white%20sash%2C%20friends%20laughing%20and%20making%20drinks%20together%2C%20celebration%20atmosphere%2C%20fun%20party%20vibes%2C%20warm%20lighting&width=600&height=400&seq=video-test-003&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    quote: 'My hens party at House of FMR was absolutely incredible! All my bridesmaids are still talking about it months later.',
    classAttended: 'Hens Party Package',
    duration: '3:12',
  },
  {
    id: 4,
    name: 'Marcus Williams',
    role: 'Aspiring Bartender',
    location: 'Perth',
    thumbnail: 'https://readdy.ai/api/search-image?query=young%20confident%20man%20practicing%20cocktail%20shaking%20technique%20at%20professional%20bar%20training%2C%20focused%20expression%2C%20bartender%20training%20environment%2C%20professional%20equipment%2C%20warm%20lighting&width=600&height=400&seq=video-test-004&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    quote: 'I came in with zero experience and left with the skills to land my first bartending job. The instructors are amazing!',
    classAttended: 'Cocktail Fundamentals',
    duration: '2:45',
  },
  {
    id: 5,
    name: 'Corporate Team - Deloitte',
    role: 'Team Building Event',
    location: 'Sydney CBD',
    thumbnail: 'https://readdy.ai/api/search-image?query=corporate%20team%20building%20cocktail%20class%2C%20diverse%20group%20of%20professionals%20in%20smart%20casual%20attire%20making%20drinks%20together%2C%20modern%20venue%2C%20collaborative%20atmosphere%2C%20professional%20photography&width=600&height=400&seq=video-test-005&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    quote: 'Best team building activity we have ever done. Everyone was engaged and we still use the recipes at office events!',
    classAttended: 'Corporate Team Building',
    duration: '2:18',
  },
  {
    id: 6,
    name: 'Sophie Anderson',
    role: 'Now Bar Owner',
    location: 'Gold Coast',
    thumbnail: 'https://readdy.ai/api/search-image?query=confident%20woman%20bar%20owner%20standing%20proudly%20behind%20her%20own%20cocktail%20bar%2C%20professional%20setting%2C%20successful%20entrepreneur%2C%20warm%20ambient%20lighting%2C%20achievement%20portrait&width=600&height=400&seq=video-test-006&orientation=landscape',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1',
    quote: 'House of FMR gave me the foundation to open my own bar. The business insights were just as valuable as the techniques.',
    classAttended: 'Professional Certification Program',
    duration: '4:02',
  },
];

export default function VideoTestimonials() {
  const [activeVideo, setActiveVideo] = useState<number | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const { ref: sectionRef, isVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });
  const modalRef = useRef<HTMLDivElement>(null);

  const openVideo = (id: number) => {
    setActiveVideo(id);
    document.body.style.overflow = 'hidden';
  };

  const closeVideo = () => {
    setActiveVideo(null);
    document.body.style.overflow = 'auto';
  };

  const activeTestimonial = videoTestimonials.find(t => t.id === activeVideo);

  return (
    <section className="py-28 bg-gradient-to-b from-stone-900 via-stone-900 to-stone-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl"></div>
      </div>
      
      {/* Decorative Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4a574' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-6 relative">
        {/* Section Header */}
        <div className={`text-center mb-16 scroll-animate-up ${isVisible ? 'visible' : ''}`}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-400"></div>
            <span className="text-amber-400 text-xs font-semibold tracking-[0.3em] uppercase">Real Stories</span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-400"></div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-display font-normal text-white mb-6">
            Hear From Our
            <span className="block text-amber-400 italic mt-2">Class Participants</span>
          </h2>
          
          <p className="text-stone-400 text-lg max-w-2xl mx-auto">
            Watch real testimonials from students who transformed their cocktail skills with House of FMR
          </p>
        </div>

        {/* Video Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videoTestimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`group relative scroll-animate-up ${isVisible ? 'visible' : ''}`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredCard(testimonial.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div 
                className="relative rounded-2xl overflow-hidden cursor-pointer transform transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-amber-500/20"
                onClick={() => openVideo(testimonial.id)}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video">
                  <img
                    src={testimonial.thumbnail}
                    alt={testimonial.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent"></div>
                  
                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`w-16 h-16 rounded-full bg-amber-400 flex items-center justify-center transform transition-all duration-300 ${hoveredCard === testimonial.id ? 'scale-110' : 'scale-100'}`}>
                      <i className="ri-play-fill text-3xl text-stone-900 ml-1"></i>
                    </div>
                    
                    {/* Ripple Effect */}
                    <div className={`absolute w-16 h-16 rounded-full border-2 border-amber-400/50 animate-ping ${hoveredCard === testimonial.id ? 'opacity-100' : 'opacity-0'}`}></div>
                  </div>
                  
                  {/* Duration Badge */}
                  <div className="absolute top-4 right-4 px-3 py-1 bg-stone-900/80 backdrop-blur-sm rounded-full text-white text-xs font-medium flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {testimonial.duration}
                  </div>
                  
                  {/* Class Badge */}
                  <div className="absolute top-4 left-4 px-3 py-1 bg-amber-400/90 backdrop-blur-sm rounded-full text-stone-900 text-xs font-semibold">
                    {testimonial.classAttended}
                  </div>
                </div>
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  {/* Quote Preview */}
                  <p className="text-white/90 text-sm mb-3 line-clamp-2 italic">
                    "{testimonial.quote}"
                  </p>
                  
                  {/* Author */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold text-sm">{testimonial.name}</p>
                      <p className="text-stone-400 text-xs">{testimonial.role} • {testimonial.location}</p>
                    </div>
                    
                    <div className="flex items-center gap-1 text-amber-400">
                      <i className="ri-video-line text-lg"></i>
                      <span className="text-xs font-medium">Watch</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        <div className={`mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 scroll-animate-up ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '600ms' }}>
          {[
            { number: '500+', label: 'Video Reviews', icon: 'ri-video-line' },
            { number: '4.9', label: 'Average Rating', icon: 'ri-star-fill' },
            { number: '98%', label: 'Would Recommend', icon: 'ri-thumb-up-line' },
            { number: '50+', label: 'Success Stories', icon: 'ri-trophy-line' },
          ].map((stat, index) => (
            <div key={index} className="text-center p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-amber-400/30 transition-all duration-300">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-400/10 flex items-center justify-center">
                <i className={`${stat.icon} text-xl text-amber-400`}></i>
              </div>
              <p className="text-3xl font-bold text-white mb-1">{stat.number}</p>
              <p className="text-stone-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center mt-12 scroll-animate-up ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '700ms' }}>
          <p className="text-stone-400 mb-4">Ready to create your own success story?</p>
          <a 
            href="#booking" 
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 font-semibold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all duration-300 shadow-lg shadow-amber-500/25 cursor-pointer whitespace-nowrap"
          >
            <span>Book Your Class Today</span>
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo && activeTestimonial && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-950/95 backdrop-blur-sm"
          onClick={closeVideo}
        >
          <div 
            ref={modalRef}
            className="relative w-full max-w-4xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={closeVideo}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            
            {/* Video Container */}
            <div className="relative rounded-2xl overflow-hidden bg-stone-900 shadow-2xl">
              <div className="aspect-video">
                <iframe
                  src={activeTestimonial.videoUrl}
                  title={`${activeTestimonial.name} testimonial`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              
              {/* Info Bar */}
              <div className="p-6 bg-stone-900 border-t border-stone-800">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-white font-semibold text-lg">{activeTestimonial.name}</h3>
                    <p className="text-stone-400 text-sm">{activeTestimonial.role} • {activeTestimonial.location}</p>
                    <p className="text-amber-400 text-sm mt-1">{activeTestimonial.classAttended}</p>
                  </div>
                  <div className="flex items-center gap-1 text-amber-400">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-lg"></i>
                    ))}
                  </div>
                </div>
                <p className="text-stone-300 mt-4 italic">"{activeTestimonial.quote}"</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}
